import { useTranslation } from 'react-i18next'
import { AlertTriangle, AlertCircle, Info } from 'lucide-react'
import type { ListingAuditReport } from '@/lib/api/listing-builder.types'

interface ConversionBlockersProps {
  blockers: ListingAuditReport['conversion_blockers']
}

const SEVERITY_CONFIG: Record<string, { icon: typeof AlertTriangle; color: string; bg: string }> = {
  high: { icon: AlertTriangle, color: 'text-red-600 dark:text-red-400', bg: 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800' },
  medium: { icon: AlertCircle, color: 'text-yellow-600 dark:text-yellow-400', bg: 'bg-yellow-50 dark:bg-yellow-900/20 border-yellow-200 dark:border-yellow-800' },
  low: { icon: Info, color: 'text-primary-600 dark:text-primary-400', bg: 'bg-primary-50 dark:bg-primary-900/20 border-primary-200 dark:border-primary-800' },
}

export function ConversionBlockers({ blockers }: ConversionBlockersProps) {
  const { t } = useTranslation()
  if (!blockers?.length) {
    return (
      <div className="bg-green-50 dark:bg-green-900/20 rounded-xl border border-green-200 dark:border-green-800 p-5 text-center">
        <p className="text-sm text-green-700 dark:text-green-400">{t('amazonListing.report.noBlockers')}</p>
      </div>
    )
  }

  return (
    <div className="space-y-3">
      <h3 className="font-semibold text-neutral-900 dark:text-white">{t('amazonListing.report.conversionBlockers')}</h3>
      {blockers.map((b, i) => {
        const config = SEVERITY_CONFIG[b.severity] || SEVERITY_CONFIG.low
        const Icon = config.icon
        return (
          <div key={i} className={`rounded-xl border p-4 ${config.bg}`}>
            <div className="flex items-start gap-3">
              <Icon className={`w-5 h-5 mt-0.5 flex-shrink-0 ${config.color}`} />
              <div className="flex-1 min-w-0">
                <div className="flex flex-wrap items-center justify-between gap-1.5 sm:gap-2 mb-1">
                  <h4 className="font-medium text-sm text-neutral-900 dark:text-white">{b.issue}</h4>
                  <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${config.color}`}>{t(`amazonListing.report.severity${b.severity.charAt(0).toUpperCase()}${b.severity.slice(1)}`, b.severity)}</span>
                </div>
                {b.perception_gap && (
                  <p className="text-xs italic text-neutral-600 dark:text-neutral-400 border-l-2 border-neutral-300 pl-2 my-1">{b.perception_gap}</p>
                )}
                <p className="text-sm text-neutral-600 dark:text-neutral-400">{b.recommendation}</p>
                {b.related_review_aspects?.length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-2">
                    {b.related_review_aspects.map((a, k) => (
                      <span key={k} className="px-1.5 py-0.5 rounded text-xs bg-white/50 dark:bg-neutral-700 text-neutral-600 dark:text-neutral-300">{a}</span>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        )
      })}
    </div>
  )
}
